import { useState, type ReactNode } from 'react'

interface ExpandableCardProps {
  title: string
  subtitle?: string
  children: ReactNode
  defaultOpen?: boolean
  headerContent?: ReactNode
}

export default function ExpandableCard({
  title,
  subtitle,
  children,
  defaultOpen = false,
  headerContent,
}: ExpandableCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div
      className={`
        border border-slate-200 rounded-xl overflow-hidden
        transition-all duration-300
        ${isOpen ? 'bg-white' : 'bg-slate-50'}
      `}
    >
      {/* Header - Clickable */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {headerContent}
            <div>
              <h3 className="text-base font-semibold text-brand-light">{title}</h3>
              {subtitle && (
                <p className="text-sm text-brand-muted mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-brand-muted transition-transform duration-300 flex-shrink-0 ml-4 ${
            isOpen ? 'rotate-180' : ''
          }`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Content - Expandable */}
      <div
        className={`
          overflow-hidden transition-all duration-400 ease-out
          ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="px-5 pb-5 pt-0">
          {children}
        </div>
      </div>
    </div>
  )
}
